import { useRef, useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/api';
import { supabase } from '../lib/supabaseClient';
import { getApiUrl } from '../lib/config';

interface StreamData {
  title: string;
  stream: MediaStream;
  source: 'camera' | 'screen';
}

interface AdminPanelProps {
  isVisible: boolean;
  onClose: () => void;
  onStreamStateChange: (isLive: boolean, streamData?: StreamData) => void;
}

interface User {
  id: string;
  username: string;
  email: string;
  display_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

interface Video {
  id: number;
  title: string;
  description: string;
  file_path: string;
  thumbnail_url: string;
  view_count: number;
  upload_date: string;
  uploader_name: string;
  status: string;
  genre: string;
  tags: string;
  duration_seconds?: number;
  bunny_video_id?: string;
}

interface LiveStream {
  id: number;
  title: string;
  description: string;
  stream_key: string;
  stream_url: string;
  rtmpUrl: string;
  playbackUrl: string;
  status: string;
}

const AdminPanel: React.FC<AdminPanelProps> = ({
  isVisible,
  onClose,
  onStreamStateChange
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [streamSource, setStreamSource] = useState<'camera' | 'screen'>('camera');
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [currentStream, setCurrentStream] = useState<MediaStream | null>(null);
  const [streamTitle, setStreamTitle] = useState('Live Stream');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  // Data states
  const [users, setUsers] = useState<User[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [stats, setStats] = useState<any>({});
  const [liveStream, setLiveStream] = useState<LiveStream | null>(null);

  // Authentication state
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  // Form states
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    genre: '',
    tags: '',
    videoFile: null as File | null,
    thumbnailFile: null as File | null
  });

  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    display_name: '',
    password: '',
    role: 'viewer'
  });

  const [streamForm, setStreamForm] = useState({
    title: '',
    description: ''
  });

  const stopCurrentStream = useCallback(() => {
    if (currentStream) {
      currentStream.getTracks().forEach(track => track.stop());
      setCurrentStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [currentStream]);

  const startPreview = useCallback(async () => {
    try {
      stopCurrentStream();
      
      let mediaStream: MediaStream;

      if (streamSource === 'camera') {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: videoEnabled,
          audio: audioEnabled
        });
      } else {
        mediaStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: audioEnabled
        });
      }

      setCurrentStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play().catch(console.error);
      }
      setIsPreviewing(true);
    } catch (err) {
      console.error('Error accessing media devices:', err);
      alert('Could not access camera/microphone. Please check permissions.');
    }
  }, [streamSource, videoEnabled, audioEnabled, stopCurrentStream]);

  const stopPreview = useCallback(() => {
    stopCurrentStream();
    setIsPreviewing(false);
  }, [stopCurrentStream]);

  const startStream = useCallback(async () => {
    if (!currentStream) {
      await startPreview();
    }
    
    if (currentStream) {
      setIsStreaming(true);
      
      // Notify parent component that stream is live
      onStreamStateChange(true, {
        title: streamTitle,
        stream: currentStream,
        source: streamSource
      });
      
      // Simulate viewer count updates
      const interval = setInterval(() => {
        // This was using setViewerCount which is no longer defined
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [currentStream, streamTitle, streamSource, onStreamStateChange, startPreview]);

  const stopStream = useCallback(() => {
    setIsStreaming(false);
    stopPreview();
    onStreamStateChange(false);
  }, [stopPreview, onStreamStateChange]);

  const toggleVideo = useCallback(() => {
    if (currentStream) {
      const videoTrack = currentStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  }, [currentStream]);

  const toggleAudio = useCallback(() => {
    if (currentStream) {
      const audioTrack = currentStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  }, [currentStream]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const { data: { user }, error } = await supabase.auth.signInWithPassword({
        email: loginForm.username,
        password: loginForm.password,
      });

      if (error) throw error;

      if (!user) {
        setLoginError('Invalid credentials');
        return;
      }

      // Get user data from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role, username')
        .eq('id', user.id)
        .single();

      if (userError || userData.role !== 'admin') {
        setLoginError('Admin access required');
        return;
      }

      setAuthToken(user.id);
      setIsAuthenticated(true);
      setLoginError('');
      localStorage.setItem('admin_token', user.id);
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('username', userData.username || 'Admin');
      loadDashboardData();
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setAuthToken(null);
    setLoginForm({ username: '', password: '' });
    setLoginError('');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
  };

  useEffect(() => {
    return () => {
      stopCurrentStream();
    };
  }, [stopCurrentStream]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isVisible, onClose]);

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setAuthToken(savedToken);
      setIsAuthenticated(true);
      localStorage.setItem('isAuthenticated', 'true');
      loadDashboardData();
    }
  }, []);

  const loadDashboardData = async () => {
    try {
      const [
        { data: statsData },
        { data: videosData },
        { data: usersData }
      ] = await Promise.all([
        supabase.from('analytics').select('*').limit(1),
        supabase.from('videos').select('*').order('created_at', { ascending: false }),
        supabase.from('users').select('*').order('created_at', { ascending: false })
      ]);

      setStats(statsData || {});
      setVideos(videosData || []);
      setUsers(usersData || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const handleVideoUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoForm.videoFile || !videoForm.title) {
      alert('Please provide title and video file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('video', videoForm.videoFile);
      if (videoForm.thumbnailFile) {
        formData.append('thumbnail', videoForm.thumbnailFile);
      }
      formData.append('title', videoForm.title);
      formData.append('description', videoForm.description);
      formData.append('genre', videoForm.genre);
      formData.append('tags', JSON.stringify(videoForm.tags.split(',').map(t => t.trim()).filter(t => t)));

      // Get Supabase JWT
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;

      const response = await fetch(getApiUrl('api/videos'), {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        alert('Video uploaded successfully to Bunny.net!');
        setVideoForm({
          title: '',
          description: '',
          genre: '',
          tags: '',
          videoFile: null,
          thumbnailFile: null
        });
        loadDashboardData();
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.error}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId: number) => {
    if (!confirm('Are you sure you want to delete this video?')) return;

    try {
      const response = await fetch(`/api/videos/${videoId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Video deleted successfully');
        loadDashboardData();
      } else {
        alert('Failed to delete video');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete video');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userForm.username || !userForm.email || !userForm.password) {
      alert('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      // First create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userForm.email,
        password: userForm.password,
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error('Failed to create auth user');
      }

      // Then create user record
      const { data: userData, error: userError } = await supabase
        .from('users')
        .insert([{
          id: authData.user.id,
          username: userForm.username,
          email: userForm.email,
          display_name: userForm.display_name,
          role: userForm.role,
          is_active: true
        }])
        .select()
        .single();

      if (userError) throw userError;

      alert('User created successfully');
      setUserForm({
        username: '',
        email: '',
        display_name: '',
        password: '',
        role: 'viewer'
      });
      loadDashboardData();
    } catch (error) {
      console.error('Create user error:', error);
      alert('Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      // Soft delete in users table
      const { error: userError } = await supabase
        .from('users')
        .update({ is_active: false })
        .eq('id', userId);

      if (userError) throw userError;

      alert('User deleted successfully');
      loadDashboardData();
    } catch (error) {
      console.error('Delete user error:', error);
      alert('Failed to delete user');
    }
  };

  const handleCreateLiveStream = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!streamForm.title) {
      alert('Please provide stream title');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/videos/live-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(streamForm),
      });

      if (response.ok) {
        const data = await response.json();
        setLiveStream({
          id: data.stream.id,
          title: streamForm.title,
          description: streamForm.description,
          stream_key: data.bunnyStream.streamKey,
          stream_url: data.bunnyStream.playbackUrl,
          rtmpUrl: data.bunnyStream.rtmpUrl,
          playbackUrl: data.bunnyStream.playbackUrl,
          status: 'live'
        });
        alert('Live stream created successfully!');
        setStreamForm({ title: '', description: '' });
      } else {
        const error = await response.json();
        alert(`Failed to create stream: ${error.error}`);
      }
    } catch (error) {
      console.error('Create stream error:', error);
      alert('Failed to create live stream');
    } finally {
      setLoading(false);
    }
  };

  // Duration management functions
  const handleUpdateVideoDuration = async (videoId: number) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/videos/${videoId}/update-duration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        // Update the video in the local state
        setVideos(videos.map(video => 
          video.id === videoId 
            ? { ...video, duration_seconds: data.duration }
            : video
        ));
        alert(`Duration updated successfully: ${data.duration} seconds`);
      } else {
        const error = await response.json();
        alert(`Failed to update duration: ${error.error}`);
      }
    } catch (error) {
      console.error('Update duration error:', error);
      alert('Failed to update video duration');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAllMetadata = async () => {
    if (!confirm('This will sync metadata for all videos from Bunny Stream. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/videos/sync-metadata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Metadata sync completed!\n` +
          `✅ ${data.successfulUpdates} videos updated\n` +
          `⏳ ${data.videosWithoutDuration} videos still processing\n` +
          `❌ ${data.errors} errors`);
        
        // Reload the videos list
        loadDashboardData();
      } else {
        const error = await response.json();
        alert(`Failed to sync metadata: ${error.error}`);
      }
    } catch (error) {
      console.error('Sync metadata error:', error);
      alert('Failed to sync video metadata');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'Unknown';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleProcessForStreaming = async (videoId: number) => {
    if (!confirm('This will process the video through Bunny Stream to get duration. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/videos/${videoId}/process-for-streaming`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Video is now being processed for streaming!\n` +
          `Bunny Video ID: ${data.bunnyVideoId}\n` +
          `Duration will be available in a few minutes.`);
        
        // Reload the videos list
        loadDashboardData();
      } else {
        const error = await response.json();
        alert(`Failed to process video: ${error.error}`);
      }
    } catch (error) {
      console.error('Process for streaming error:', error);
      alert('Failed to process video for streaming');
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-200">
        <div className="bg-white shadow-xl rounded-xl p-8 w-full max-w-md border border-blue-100">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-extrabold text-blue-700 mb-2">🎛️ Admin Login</h1>
            <p className="text-gray-500">Enter your credentials to access the admin panel</p>
          </div>
          {loginError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 rounded text-red-700 text-center">
              {loginError}
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold mb-1 text-blue-700">Email</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                required
                disabled={loading}
                placeholder="admin@radiostation.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1 text-blue-700">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                required
                disabled={loading}
                placeholder="••••••••"
              />
            </div>
            <button 
              type="submit" 
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow transition"
              disabled={loading}
            >
              {loading ? 'Logging in...' : '🔐 Login'}
            </button>
          </form>
          <div className="mt-6 text-center text-xs text-gray-400">
            <p>Need an account? Contact your system administrator.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-blue-100 shadow-lg flex flex-col min-h-screen sticky top-0 z-10">
        <div className="p-6 border-b border-blue-100">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎛️</span>
            <span className="font-extrabold text-lg text-blue-700">Admin Panel</span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'videos', label: 'Videos', icon: '🎬' },
            { id: 'users', label: 'Users', icon: '👥' },
            { id: 'livestream', label: 'Live', icon: '📺' },
            { id: 'settings', label: 'Settings', icon: '⚙️' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg font-semibold transition-colors text-left ${
                activeTab === tab.id
                  ? 'bg-blue-100 text-blue-800 shadow'
                  : 'hover:bg-blue-50 text-gray-700'
              }`}
            >
              <span className="text-xl">{tab.icon}</span> {tab.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-blue-100">
          <button onClick={handleLogout} className="w-full py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold shadow transition">
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        {loading && (
          <div className="fixed top-4 right-4 bg-blue-100 border border-blue-400 rounded p-3 shadow-lg z-50">
            Loading...
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <section className="space-y-8">
            <h2 className="text-3xl font-extrabold text-blue-700 mb-4 flex items-center gap-2">📊 Dashboard Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { label: 'Total Videos', value: stats.totalVideos || 0, icon: '🎬' },
                { label: 'Total Users', value: stats.totalUsers || 0, icon: '👥' },
                { label: 'Total Views', value: stats.totalViews || 0, icon: '👁️' },
                { label: 'Live Streams', value: stats.liveStreams || 0, icon: '📺' },
              ].map(stat => (
                <div key={stat.label} className="bg-white rounded-xl shadow p-6 flex flex-col items-center border border-blue-100">
                  <div className="text-4xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-bold text-blue-700">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl shadow p-6 border border-blue-100">
              <h3 className="font-semibold mb-3 text-blue-700">🔗 Quick Links</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button 
                  onClick={() => setActiveTab('videos')}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold py-3 rounded-lg shadow transition"
                >
                  📹 Upload New Video
                </button>
                <button 
                  onClick={() => setActiveTab('livestream')}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold py-3 rounded-lg shadow transition"
                >
                  📺 Start Live Stream
                </button>
                <button 
                  onClick={() => setActiveTab('users')}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold py-3 rounded-lg shadow transition"
                >
                  👤 Create New User
                </button>
                <button 
                  onClick={() => window.open('/api/test-bunny/config', '_blank')}
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold py-3 rounded-lg shadow transition"
                >
                  🐰 Bunny.net Status
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Video Management Tab */}
        {activeTab === 'videos' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">🎬 Video Management</h2>

            {/* Upload Form */}
            <div className="window-inset p-6">
              <h3 className="font-semibold mb-4">📤 Upload New Video</h3>
              <form onSubmit={handleVideoUpload} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Title *</label>
                    <input
                      type="text"
                      value={videoForm.title}
                      onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Genre</label>
                    <select
                      value={videoForm.genre}
                      onChange={(e) => setVideoForm({...videoForm, genre: e.target.value})}
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                    >
                      <option value="">Select Genre</option>
                      <option value="Rock">Rock</option>
                      <option value="Jazz">Jazz</option>
                      <option value="Electronic">Electronic</option>
                      <option value="Classical">Classical</option>
                      <option value="Talk Show">Talk Show</option>
                      <option value="News">News</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <textarea
                    value={videoForm.description}
                    onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                    className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400 min-h-24 resize-none"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={videoForm.tags}
                    onChange={(e) => setVideoForm({...videoForm, tags: e.target.value})}
                    className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                    placeholder="live, acoustic, interview"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Video File * (Max 10GB)</label>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => setVideoForm({...videoForm, videoFile: e.target.files?.[0] || null})}
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Thumbnail (Optional)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setVideoForm({...videoForm, thumbnailFile: e.target.files?.[0] || null})}
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                    />
                  </div>
                </div>

                <button type="submit" className="px-4 py-2 rounded-lg font-semibold shadow bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 transition" disabled={loading}>
                  {loading ? 'Uploading...' : '🚀 Upload to Bunny.net'}
                </button>
              </form>
            </div>

            {/* Videos List */}
            <div className="window-inset p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">📋 Uploaded Videos ({videos.length})</h3>
                <button
                  onClick={handleSyncAllMetadata}
                  className="px-4 py-2 rounded-lg font-semibold shadow bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 transition"
                  disabled={loading}
                >
                  🔄 Sync All Durations from Bunny
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-xl shadow overflow-hidden border border-blue-100">
                  <thead className="bg-blue-50 text-blue-700 uppercase text-xs font-bold">
                    <tr>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Title</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Genre</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Duration</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Views</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Upload Date</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-50">
                    {videos.map(video => (
                      <tr key={video.id} className="bg-white hover:bg-blue-50 transition">
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">{video.title}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">{video.genre}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">
                          <div className={`font-semibold ${!video.duration_seconds || video.duration_seconds === 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            {formatDuration(video.duration_seconds)}
                          </div>
                          {!video.bunny_video_id && (!video.duration_seconds || video.duration_seconds === 0) && (
                            <button
                              onClick={() => handleProcessForStreaming(video.id)}
                              className="px-2 py-1 rounded font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs transition"
                              disabled={loading}
                            >
                              🎬 Process for Streaming
                            </button>
                          )}
                          {video.bunny_video_id && (!video.duration_seconds || video.duration_seconds === 0) && (
                            <button
                              onClick={() => handleUpdateVideoDuration(video.id)}
                              className="px-2 py-1 rounded font-semibold bg-orange-100 text-orange-700 hover:bg-orange-200 text-xs transition"
                              disabled={loading}
                            >
                              🔄 Update Duration
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">{video.view_count}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">
                          {new Date(video.upload_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDeleteVideo(video.id)}
                              className="px-4 py-2 rounded-lg font-semibold shadow bg-red-500 text-white hover:bg-red-600 focus:ring-2 focus:ring-red-400 transition"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">👥 User Management</h2>

            {/* Create User Form */}
            <div className="window-inset p-6">
              <h3 className="font-semibold mb-4">👤 Create New User</h3>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Username *</label>
                    <input
                      type="text"
                      value={userForm.username}
                      onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Email *</label>
                    <input
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Display Name</label>
                    <input
                      type="text"
                      value={userForm.display_name}
                      onChange={(e) => setUserForm({...userForm, display_name: e.target.value})}
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Role</label>
                    <select
                      value={userForm.role}
                      onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                      className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="host">Host</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Password *</label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                    className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                    required
                  />
                </div>

                <button type="submit" className="px-4 py-2 rounded-lg font-semibold shadow bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 transition">
                  ➕ Create User
                </button>
              </form>
            </div>

            {/* Users List */}
            <div className="window-inset p-6">
              <h3 className="font-semibold mb-4">All Users ({users.length})</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-xl shadow overflow-hidden border border-blue-100">
                  <thead className="bg-blue-50 text-blue-700 uppercase text-xs font-bold">
                    <tr>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Username</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Email</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Role</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Status</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Created</th>
                      <th className="px-4 py-3 text-left border-b border-blue-100">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-50">
                    {users.map(user => (
                      <tr key={user.id} className="bg-white hover:bg-blue-50 transition">
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">{user.username}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">{user.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.role === 'admin' ? 'bg-red-100 text-red-800' :
                            user.role === 'host' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-blue-50">
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="px-4 py-2 rounded-lg font-semibold shadow bg-red-500 text-white hover:bg-red-600 focus:ring-2 focus:ring-red-400 transition"
                            disabled={user.role === 'admin'}
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Live Stream Tab */}
        {activeTab === 'livestream' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">📺 Live Streaming Control</h2>

            {/* Create Stream Form */}
            <div className="window-inset p-6">
              <h3 className="font-semibold mb-4">🎬 Create Live Stream</h3>
              <form onSubmit={handleCreateLiveStream} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Stream Title *</label>
                  <input
                    type="text"
                    value={streamForm.title}
                    onChange={(e) => setStreamForm({...streamForm, title: e.target.value})}
                    className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400"
                    placeholder="Live Radio Show - Rock Music"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <textarea
                    value={streamForm.description}
                    onChange={(e) => setStreamForm({...streamForm, description: e.target.value})}
                    className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white shadow-sm placeholder-gray-400 min-h-24 resize-none"
                    placeholder="Description of your live stream..."
                    rows={3}
                  />
                </div>

                <button type="submit" className="px-4 py-2 rounded-lg font-semibold shadow bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 transition" disabled={loading}>
                  {loading ? 'Creating...' : '🚀 Create Live Stream'}
                </button>
              </form>
            </div>

            {/* Stream Info */}
            {liveStream && (
              <div className="window-inset p-6">
                <h3 className="font-semibold mb-4">📡 Stream Configuration</h3>
                <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
                  <div className="text-green-800 font-semibold mb-2">
                    ✅ Live stream created successfully!
                  </div>
                  <div className="text-sm text-green-700">
                    Use the RTMP details below in OBS Studio or your GoPro camera
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold mb-1">RTMP Server URL</label>
                      <div className="input-3d bg-gray-50 p-2 font-mono text-sm break-all">
                        {liveStream.rtmpUrl}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold mb-1">Stream Key</label>
                      <div className="input-3d bg-gray-50 p-2 font-mono text-sm break-all">
                        {liveStream.stream_key}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Viewer URL</label>
                      <div className="input-3d bg-gray-50 p-2 font-mono text-sm break-all">
                        {liveStream.playbackUrl}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <button
                        onClick={() => navigator.clipboard.writeText(liveStream.rtmpUrl)}
                        className="px-3 py-1 rounded font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs transition"
                      >
                        📋 Copy RTMP URL
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText(liveStream.stream_key)}
                        className="px-3 py-1 rounded font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs transition"
                      >
                        🔑 Copy Stream Key
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
                  <h4 className="font-semibold text-blue-800 mb-2">📱 OBS Studio Setup:</h4>
                  <ol className="text-sm text-blue-700 space-y-1">
                    <li>1. Open OBS Studio</li>
                    <li>2. Go to Settings → Stream</li>
                    <li>3. Service: Custom</li>
                    <li>4. Server: Copy RTMP URL above</li>
                    <li>5. Stream Key: Copy Stream Key above</li>
                    <li>6. Click "Start Streaming"</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">⚙️ System Settings</h2>

            <div className="window-inset p-6">
              <h3 className="font-semibold mb-4">🔧 Configuration</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">🐰 Bunny.net Status</h4>
                    <div className="space-y-2">
                      <button
                        onClick={() => window.open('/api/test-bunny/config', '_blank')}
                        className="px-3 py-1 rounded font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs transition"
                      >
                        🔍 Check Configuration
                      </button>
                      <button
                        onClick={() => window.open('/api/test-bunny/storage/list', '_blank')}
                        className="px-3 py-1 rounded font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs transition"
                      >
                        📁 List Storage Files
                      </button>
                      <button
                        onClick={() => window.open('/api/test-bunny/stream/videos', '_blank')}
                        className="px-3 py-1 rounded font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs transition"
                      >
                        🎬 Check Stream Videos
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">📊 System Info</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Server Status:</span>
                        <span className="text-green-600 font-semibold">🟢 Online</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Database:</span>
                        <span className="text-green-600 font-semibold">🟢 Connected</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Storage:</span>
                        <span className="text-green-600 font-semibold">🐰 Bunny.net</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Streaming:</span>
                        <span className="text-green-600 font-semibold">🎬 Bunny Stream</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">🔒 Security</h4>
                  <div className="text-sm text-gray-600">
                    <p>• Admin panel access is password protected</p>
                    <p>• Video uploads restricted to administrators</p>
                    <p>• Public video viewing with optional chat registration</p>
                    <p>• Secure cloud storage with Bunny.net</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel; 